import React, { useCallback, useMemo, useState } from 'react';
import { ContextMenu, ContextMenuBuilder, type RepeatRowsPayload } from '@/components/custom/erp/input-table-v2';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { useAutoSelectBoPhan } from './hooks/useAutoSelectBoPhan';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import type { AccountConstraint } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { useDetailPaste } from '../hooks';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
  isPasting?: boolean;
  constraints?: AccountConstraint[];
}

const DETAIL_ACTION_ORDER = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'] as const;
const noop = () => {};
const detail = (row: Record<string, MuaHangRecord>) => constraintValues(row);

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  onRepeatForRows,
  isPasting,
  constraints = []
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: MuaHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const { fetchDetail } = useCRUD({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });
  const handleCellValueChange = useAutoSelectBoPhan(onCellValueChange);
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });
  const columns = useMemo(
    () => getDetailTableColumns(formMode, handleCellValueChange, fetchDetail, cellError),
    [cellError, formMode, handleCellValueChange, fetchDetail]
  );
  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });
  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );
  const handleRowId = useCallback((row: MuaHangRecord) => row?.uuid || '', []);
  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams) => {
      onCellClick?.(params);

      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick as MuaHangRecord}
        onCellClick={onCellClick as MuaHangRecord}
        onCellContextMenu={handleCellContextMenu as MuaHangRecord}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={handleRowId}
        pasteTarget={selectedCell}
        actionLoading={isPasting}
        actionsDisabled={formMode === 'view'}
        actionOrder={DETAIL_ACTION_ORDER as MuaHangRecord}
        actions={tableActions}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() => {
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          });
        }}
      />
    </>
  );
};

export default DetailTab;
